import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderPlus, FolderOpen, FolderUp, Pencil, Trash2, Check, X, Loader2, Search, Images, AlertCircle, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';
import PhotoFrame from '../components/PhotoFrame';
import AlbumBulkUploader from '../components/AlbumBulkUploader';

const inputCls = 'w-full px-3 py-2 rounded border text-sm';
const border = { borderColor: 'rgba(0,0,0,0.2)' };
const SORTS = [['name', 'Name A–Z'], ['count', 'Most photos'], ['latest', 'Latest photos'], ['created', 'Newest album']];

function CreateAlbum({ onCreated }) {
  const { adminCreateAlbum } = useAuth();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try { await adminCreateAlbum({ name: name.trim(), description: desc.trim() }); toast.success(`Album “${name.trim()}” created.`); setName(''); setDesc(''); onCreated(name.trim()); }
    catch (err) { toast.error(formatApiError(err, 'Could not create the album.')); }
    setBusy(false);
  };
  return (
    <form onSubmit={create} className="rounded-lg border bg-white p-4 grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid="albums-create-form">
      <label className="block"><span className="block text-xs mb-1 font-title" style={{ color: 'var(--olive)' }}>New album name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anzac Day 2026" className={inputCls} style={border} data-testid="albums-create-name" /></label>
      <label className="block"><span className="block text-xs mb-1 font-title" style={{ color: 'var(--olive)' }}>Description (optional)</span>
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What members will see under the album title" className={inputCls} style={border} data-testid="albums-create-desc" /></label>
      <button type="submit" disabled={busy || !name.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded font-title text-sm disabled:opacity-50" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="albums-create-btn">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <FolderPlus size={15} />} Create album
      </button>
    </form>
  );
}

function AlbumRow({ a, onChanged }) {
  const { adminUpdateAlbum, adminDeleteAlbum, mediaUrl } = useAuth();
  const [edit, setEdit] = useState(null);
  const [upload, setUpload] = useState(false);
  const [busy, setBusy] = useState('');
  const save = async () => {
    setBusy('save');
    try { await adminUpdateAlbum(a.name, { name: edit.name.trim(), description: edit.description }); toast.success('Album updated.'); setEdit(null); onChanged(); }
    catch (e) { toast.error(formatApiError(e, 'Could not update the album.')); }
    setBusy('');
  };
  const del = async () => {
    if (!window.confirm(`Delete the album “${a.name}”?\n\nIts ${a.count} photo${a.count !== 1 ? 's' : ''} stay in the gallery and in any other albums.`)) return;
    setBusy('del');
    try { await adminDeleteAlbum(a.name); toast.success('Album deleted — photos kept.'); onChanged(); }
    catch (e) { toast.error(formatApiError(e, 'Could not delete the album.')); }
    setBusy('');
  };
  const open = `/members/photos/album/${encodeURIComponent(a.name)}?from=admin`;
  return (
    <div className="flex gap-4 items-start rounded-lg border bg-white p-3" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`albums-row-${a.name}`}>
      <Link to={open} className="shrink-0 w-28 h-20 rounded overflow-hidden" style={{ background: 'rgba(0,0,0,0.05)' }} data-testid={`albums-cover-${a.name}`}>
        {a.cover_url ? <PhotoFrame src={mediaUrl(a.cover_url)} display={{ ...(a.cover_display || {}), fit: 'cover' }} alt={a.name} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center" style={{ color: 'var(--olive)' }}><Images size={22} /></div>}
      </Link>
      <div className="flex-1 min-w-0">
        {edit ? (
          <div className="space-y-2">
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} style={border} data-testid={`albums-edit-name-${a.name}`} />
            <input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Description" className={inputCls} style={border} data-testid={`albums-edit-desc-${a.name}`} />
            <div className="flex gap-2">
              <button onClick={save} disabled={busy === 'save' || !edit.name.trim()} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid={`albums-edit-save-${a.name}`}><Check size={12} /> Save</button>
              <button onClick={() => setEdit(null)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs" style={{ background: 'var(--sage)' }}><X size={12} /> Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <Link to={open} className="font-title text-lg hover:underline" style={{ color: 'var(--navy)' }} data-testid={`albums-name-${a.name}`}>{a.name}</Link>
            <p className="text-xs" style={{ color: 'var(--olive)' }} data-testid={`albums-meta-${a.name}`}>
              {a.count} photo{a.count !== 1 ? 's' : ''}{a.latest ? ` · latest ${String(a.latest).slice(0, 10)}` : ''}
              {a.pending > 0 && <span className="ml-2 inline-flex items-center gap-1" style={{ color: '#8a1f11' }}><AlertCircle size={11} /> {a.pending} not yet visible to members</span>}
            </p>
            {a.description && <p className="text-sm mt-1 truncate" style={{ color: 'var(--ink)' }}>{a.description}</p>}
          </>
        )}
      </div>
      {!edit && (
        <div className="flex flex-col gap-1.5 shrink-0">
          <Link to={open} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid={`albums-open-${a.name}`}><FolderOpen size={12} /> Open &amp; manage photos</Link>
          <button onClick={() => setUpload(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid={`albums-upload-${a.name}`}><FolderUp size={12} /> Upload folder</button>
          <button onClick={() => setEdit({ name: a.name, description: a.description || '' })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }} data-testid={`albums-rename-${a.name}`}><Pencil size={12} /> Rename / describe</button>
          <button onClick={del} disabled={busy === 'del'} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs border" style={{ borderColor: '#8a1f11', color: '#8a1f11' }} data-testid={`albums-delete-${a.name}`}>{busy === 'del' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete album</button>
        </div>
      )}
      {upload && <AlbumBulkUploader album={a.name} onClose={() => setUpload(false)} onDone={onChanged} />}
    </div>
  );
}

// Admin → Albums: create and delete albums, then open one to add, sort, edit and delete its photos.
export default function AlbumsAdminTab() {
  const { adminAlbums } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('name');
  const load = () => adminAlbums().then(setRows).catch(() => { setRows([]); toast.error('Could not load albums.'); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const list = (rows || []).filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()) || (a.description || '').toLowerCase().includes(q.toLowerCase()));
    const key = { name: (a) => a.name.toLowerCase(), count: (a) => -a.count, latest: (a) => a.latest || '', created: (a) => a.created_at || '' }[sort];
    const sorted = [...list].sort((a, b) => (key(a) > key(b) ? 1 : key(a) < key(b) ? -1 : 0));
    return sort === 'latest' || sort === 'created' ? sorted.reverse() : sorted;
  }, [rows, q, sort]);

  return (
    <div className="space-y-4" data-testid="albums-admin">
      <div>
        <h2 className="font-title text-xl flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Images size={20} /> Albums</h2>
        <p className="text-sm" style={{ color: 'var(--olive)' }}>Create or delete albums here, then open an album to add photos, drag or sort them into order, edit details or images, and remove or delete photos. Deleting an album never deletes its photos.</p>
      </div>
      <CreateAlbum onCreated={load} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]"><Search size={14} className="absolute left-2.5 top-2.5" style={{ color: 'var(--olive)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search albums" className={`${inputCls} pl-8`} style={border} data-testid="albums-search" /></div>
        <label className="inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--olive)' }}><ArrowUpDown size={13} />
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-2 py-2 rounded border text-sm bg-white" style={border} data-testid="albums-sort">{SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <span className="text-xs" style={{ color: 'var(--olive)' }} data-testid="albums-count">{rows ? `${shown.length} of ${rows.length} album${rows.length !== 1 ? 's' : ''}` : 'Loading…'}</span>
      </div>
      <div className="space-y-2">
        {rows && shown.length === 0 && <p className="text-sm px-3 py-6 text-center rounded" style={{ background: 'rgba(0,0,0,0.03)', color: 'var(--olive)' }} data-testid="albums-empty">No albums{q ? ' match your search' : ' yet — create one above'}.</p>}
        {shown.map((a) => <AlbumRow key={a.name} a={a} onChanged={load} />)}
      </div>
    </div>
  );
}
